import { CardContainer, Icon } from "@screencloud/screencloud-ui-components";
import styled from "styled-components";

/* ---------------------------------- Layout --------------------------------- */

export const CheckoutLayout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xl, 24px);
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: var(--spacing-xl, 24px);
  overflow-y: auto;
`;

export const ReviewColumn = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--spacing-lg, 16px);
  min-width: 0;
`;

export const SummaryColumn = styled.div`
  position: sticky;
  top: 0;
  flex: 0 0 342px;
  width: 342px;
`;

/* ------------------------------- Review card ------------------------------- */

export const ReviewCardStyled = styled(CardContainer)`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md, 12px);
  width: 100%;
  border: 1px solid var(--grey-40, #ddd);
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.1);
  cursor: default;

  &:hover {
    background: var(--color-white-100, #fff);
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm, 8px);
  width: 100%;
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-family: Lato, sans-serif;
  font-weight: 900;
  font-size: 16px;
  line-height: 24px;
  color: var(--color-gray-950, #19191b);
`;

export const EditButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
`;

export const EditIcon = styled(Icon)`
  width: 20px;
  height: 20px;
  background-color: var(--color-gray-400, #7d7e84);
`;

export const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm, 8px);
  width: 100%;
`;

export const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--spacing-sm, 8px);
  width: 100%;
`;

export const Label = styled.span`
  font-family: Lato, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: var(--color-gray-400, #7d7e84);
`;

export const Value = styled.span`
  font-family: Lato, sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  text-align: right;
  color: var(--color-gray-950, #19191b);
`;

/*
 * Greyed secondary line (e.g. "Free device -$00.00", "Shipping +$00.00").
 * nowrap keeps the free-device offer on a single line in the narrow summary
 * column rather than breaking the amount onto its own row.
 */
export const MutedRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-xxs, 4px);
  width: 100%;
  white-space: nowrap;
  font-family: Lato, sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: var(--color-gray-300, #c5c6ca);
`;

/*
 * The free-device saving line — green (Figma color/green/700) so the negative
 * amount reads as a saving, not a charge.
 */
export const SavingsRow = styled(MutedRow)`
  color: var(--color-green-700, #1b7a3a);
`;

/*
 * Green discount line ("Discount amount -$00.00") — the saving is called out in
 * the success colour per the Figma checkout design (GRWBKK-1739).
 */
export const DiscountRow = styled(MutedRow)`
  color: var(--color-green-600, #1e9b46);
`;

/* ------------------------------ Address blocks ----------------------------- */

export const AddressGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md, 12px);
  width: 100%;
`;

export const AddressBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const AddressLabel = styled.span`
  font-family: Lato, sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-gray-950, #19191b);
`;

export const AddressText = styled.span`
  font-family: Lato, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-foreground, #404145);
`;

/* ------------------------------ Order summary ------------------------------ */

export const OrderSummaryCardStyled = styled(CardContainer)`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg, 16px);
  width: 100%;
  border: none;
  border-radius: var(--radius-md, 8px);
  background: var(--color-white-100, #fff);
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
  cursor: default;

  &:hover {
    background: var(--color-white-100, #fff);
  }
`;

/* Header group: title + description, tight 4px gap. */
export const SummaryHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs, 4px);
  width: 100%;
`;

export const SummaryHeading = styled.h3`
  margin: 0;
  font-family: Lato, sans-serif;
  font-weight: 900;
  font-size: 16px;
  line-height: 24px;
  color: var(--color-gray-950, #19191b);
`;

export const SummaryDescription = styled.p`
  margin: 0;
  font-family: Lato, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-gray-400, #7d7e84);
`;

/* Line items stack flush; each row is icon (left) + right-aligned text column. */
export const SummaryItems = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const LineItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const LineItemImageWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 117px;
  height: 82px;
  flex-shrink: 0;
`;

export const LineItemImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 100%;
`;

/* Right column: caption / title / price stacked, right-aligned, in a 130px box. */
export const LineItemColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--spacing-xxxs, 2px);
  width: 130px;
  text-align: right;
`;

export const LineItemCaption = styled.span`
  font-family: Lato, sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-gray-300, #a1a2a7);
`;

export const LineItemTitle = styled.span`
  font-family: Lato, sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-gray-950, #19191b);
`;

export const LineItemPrice = styled.span`
  width: 100%;
  font-family: Lato, sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  text-align: right;
  white-space: nowrap;
  color: var(--color-gray-200, #c5c6ca);
`;

/* The free-device saving amount — green (Figma color/green/700), see SavingsRow. */
export const LineItemSaving = styled(LineItemPrice)`
  color: var(--color-green-700, #1b7a3a);
`;

/* Green negative discount line under the licences price (GRWBKK-1739). */
export const DiscountLineItemPrice = styled(LineItemPrice)`
  color: var(--color-green-600, #1e9b46);
`;

/* Total row: no icon, right-aligned column; the amount uses the active foreground. */
export const TotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

export const TotalValue = styled.span`
  width: 100%;
  font-family: Lato, sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  text-align: right;
  color: var(--color-foreground-active, #19191b);
`;

export const SummaryDivider = styled.hr`
  width: 100%;
  height: 1px;
  margin: 0;
  border: 0;
  background: var(--color-gray-100, #e2e3e5);
`;

/* Purchase button row: right-aligned, button keeps its natural (auto) width. */
export const PurchaseRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

/* Charge/SCA failure message shown above the Purchase button (useError). */
export const PurchaseError = styled.p`
  margin: 0;
  font-family: Lato, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  text-align: right;
  color: var(--color-destructive, #d0021b);
`;
